import { useState } from 'react';
import { Navigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Form, Input, Button, Typography, Alert, Space, Spin } from 'antd';
import { useAuth } from '../app/AuthContext';

const { Title } = Typography;

interface LocationState {
  from?: {
    pathname: string;
  };
}

export default function Login() {
  const { login, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as LocationState)?.from?.pathname || '/';

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" tip="Loading...">
          <div style={{ height: 100 }} />
        </Spin>
      </div>
    );
  }

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (values: { email: string; password: string }) => {
    setError(null);
    setLoading(true);

    try {
      await login(values.email, values.password);
      // Navigation will happen automatically via AuthContext
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#000000',
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 480 }}>
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <div
            style={{
              background: '#1a1a1a',
              borderRadius: '12px',
              padding: '48px',
              border: '1px solid #2a2a2a',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '32px' }}>
              <img 
                src="/images/logo.png" 
                alt="DivvyStash" 
                style={{ 
                  maxWidth: '200px',
                  height: 'auto',
                  marginBottom: '24px'
                }} 
              />
              <Title
                level={2}
                style={{
                  color: '#ffffff',
                  fontSize: '32px',
                  fontWeight: 600,
                  marginBottom: 0,
                }}
              >
                Log in
              </Title>
            </div>

            {error && (
              <Alert
                title={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: '24px' }}
              />
            )}

            <Form
              name="login"
              onFinish={handleSubmit}
              autoComplete="off"
              layout="vertical"
            >
              <Form.Item
                name="email"
                label={<span style={{ color: '#ffffff', fontSize: '14px' }}>Email</span>}
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input
                  placeholder=""
                  autoComplete="email"
                  autoFocus
                  style={{
                    background: '#000000',
                    border: '1px solid #2a2a2a',
                    color: '#ffffff',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    height: '48px',
                  }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={<span style={{ color: '#ffffff', fontSize: '14px' }}>Password</span>}
                rules={[{ required: true, message: 'Please enter your password' }]}
              >
                <Input.Password
                  placeholder=""
                  autoComplete="current-password"
                  style={{
                    background: '#000000',
                    border: '1px solid #2a2a2a',
                    color: '#ffffff',
                    borderRadius: '8px',
                    fontSize: '14px',
                    height: '48px',
                  }}
                  styles={{
                    input: {
                      background: '#000000',
                      color: '#ffffff',
                    },
                  }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: '16px' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  style={{
                    height: '48px',
                    fontSize: '16px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    background: '#1677ff',
                    border: 'none',
                  }}
                >
                  Log in
                </Button>
              </Form.Item>

              <div style={{ textAlign: 'center' }}>
                <RouterLink to="/forgot-password" style={{ color: '#ffffff', fontSize: '14px' }}>
                  Forgot password?
                </RouterLink>
              </div>
            </Form>
          </div>
        </Space>
      </div>
    </div>
  );
}
